import * as React from 'react';

export type PreviewAlertShape = {
  message: string;
  link: string | null;
  level: string;
};

type AdminAlertPreviewContextValue = {
  previewAlert: PreviewAlertShape | null;
  setPreviewAlert: (value: PreviewAlertShape | null) => void;
  dismissPreview: () => void;
  registerPreviewDismissSideEffect: (fn: (() => void) | null) => void;
};

const AdminAlertPreviewContext = React.createContext<AdminAlertPreviewContextValue | null>(null);

const AdminAlertPreviewProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const [previewAlert, setPreviewAlert] = React.useState<PreviewAlertShape | null>(null);
  const dismissSideEffectRef = React.useRef<(() => void) | null>(null);

  const registerPreviewDismissSideEffect = React.useCallback((fn: (() => void) | null) => {
    dismissSideEffectRef.current = fn;
  }, []);

  const dismissPreview = React.useCallback(() => {
    dismissSideEffectRef.current?.();
    setPreviewAlert(null);
  }, []);

  const value = React.useMemo(
    () => ({
      previewAlert,
      setPreviewAlert,
      dismissPreview,
      registerPreviewDismissSideEffect,
    }),
    [previewAlert, dismissPreview, registerPreviewDismissSideEffect]
  );

  return <AdminAlertPreviewContext.Provider value={value}>{children}</AdminAlertPreviewContext.Provider>;
};

function useAdminAlertPreview(): AdminAlertPreviewContextValue {
  const ctx = React.useContext(AdminAlertPreviewContext);
  if (!ctx) {
    return {
      previewAlert: null,
      setPreviewAlert: () => {},
      dismissPreview: () => {},
      registerPreviewDismissSideEffect: () => {},
    };
  }
  return ctx;
}

export { AdminAlertPreviewProvider, useAdminAlertPreview };
